import {
  Badge,
  Button,
  Card,
  Flex,
  Group,
  Image,
  NumberInput,
  Select,
  Space,
  Text,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconExternalLink } from "@tabler/icons-react";
import { Transaction } from "bitcoinjs-lib";
import { useMemo, useState } from "react";
import { AdminAPIClient } from "../constants";
import { useWalletContext } from "../context/wallet";
import { useTokenById } from "../hooks/useTokenById";
import { useTokensByWallet } from "../hooks/useTokensByWallet";
import { walletProvider } from "../services/wallet";
import { IOrdItem } from "../types";
import { DepositButton } from "./DepositButton";
import { SelectItem } from "./SelectItem";
import { SignedPsbtPreview } from "./SignedPsbtPreview";
import { WalletConnectButton } from "./WalletConnectButton";
import { WithdrawButton } from "./WithdrawButton";

type ListingFormValues = {
  inscriptionId: string;
  reservePrice: number;
};

const getDepositTokenPsbt = async (
  req: ListingFormValues,
  walletAddress: string,
  sellerReceiveAddress: string
) => {
  const { data } = await AdminAPIClient.get("/auctions/psbt/seller", {
    params: {
      walletAddress,
      tokenIds: [req.inscriptionId],
      startValues: [Math.floor(req.reservePrice * 1e8)],
      sellerReceiveAddress,
    },
  });
  return data;
};

const PreviewImage = ({ token }: { token: IOrdItem }) => {
  const { multiSigWallet, connected } = useWalletContext();
  const ownedByMultisig = token.owner === multiSigWallet.data?.address;
  return (
    <Card shadow="sm" padding="lg" radius="md" w="50%" withBorder>
      <Card.Section>
        <Image
          src={token.meta?.high_res_img_url ?? token.contentURI}
          withPlaceholder
          height={300}
          fit="cover"
        />
      </Card.Section>

      <Group position="apart" mt="md" mb="xs">
        <Text weight={500}>
          {token.meta?.name ?? `Inscription #${token.inscriptionNumber}`}
        </Text>

        {connected && (
          <Badge color={ownedByMultisig ? "green" : "red"}>
            {ownedByMultisig ? "Owned by multisig" : "Not owned by multisig"}
          </Badge>
        )}
      </Group>

      <Button
        component="a"
        href={`https://magiceden.io/ordinals/item-details/${token.id}`}
        variant="light"
        color="blue"
        fullWidth
        mt="md"
        radius="md"
        target="_blank"
        rel="noopener noreferrer"
        leftIcon={<IconExternalLink size="1rem" />}
      >
        View item
      </Button>
    </Card>
  );
};

export const ListingForm = () => {
  const { connected, ordinalsAddress, multiSigWallet, paymentAddress } =
    useWalletContext();
  const [signedAuctionPsbt, setSignedAuctionPsbt] = useState<string>("");
  const form = useForm<ListingFormValues>({
    validateInputOnChange: true,

    initialValues: {
      inscriptionId: "",
      reservePrice: 0,
    },

    validate: {
      inscriptionId: (val) => (!!val ? null : "Inscription ID is required"),
      reservePrice: (val) => (val <= 0 ? "Price must be positive" : null),
    },
  });
  const token = useTokenById(form.values.inscriptionId);
  const ownedByMultisig =
    token.data && token.data?.owner === multiSigWallet.data?.address;
  const walletTokens = useTokensByWallet();
  const tokenSelectOptions = useMemo(
    () =>
      walletTokens.data?.pages.flatMap((page) =>
        page.tokens.map((token) => ({
          image: token.contentURI,
          value: token.id,
          label: token.meta?.name
            ? `${token.meta?.name} (Inscription #${token.inscriptionNumber})`
            : `${token.id.slice(0, 6)}...${token.id.slice(-4)}`,
          description: token.collection?.name,
        }))
      ),
    [walletTokens.data?.pages]
  );

  return (
    <Flex gap="md">
      <form
        style={{ width: "50%" }}
        onSubmit={form.onSubmit(async (values) => {
          if (
            !connected ||
            !ordinalsAddress ||
            !multiSigWallet.data?.address ||
            !paymentAddress
          ) {
            return;
          }
          try {
            const { psbts } = await getDepositTokenPsbt(
              values,
              multiSigWallet.data.address,
              paymentAddress
            );

            const { signedPsbt } =
              await walletProvider.signTransaction(psbts[0], [
                {
                  address: paymentAddress,
                  indexesToSign: [0],
                  sigHash:
                    Transaction.SIGHASH_SINGLE |
                    Transaction.SIGHASH_ANYONECANPAY,
                },
              ]);
            setSignedAuctionPsbt(signedPsbt);
          } catch (err) {
            console.error(err);
          }
        })}
      >
        <Select
          label="Inscription"
          searchable
          itemComponent={SelectItem}
          maxDropdownHeight={300}
          data={tokenSelectOptions ?? []}
          withAsterisk
          {...form.getInputProps("inscriptionId")}
        />
        <NumberInput
          precision={6}
          label="Reserve price (BTC)"
          withAsterisk
          min={0}
          {...form.getInputProps("reservePrice")}
        />
        <Space h="md" />
        <Flex gap="sm">
          {connected && !ownedByMultisig && token.data && (
            <DepositButton tokenIds={[token.data.id]} />
          )}
          {connected && token.data && ownedByMultisig && (
            <WithdrawButton tokenIds={[token.data.id]} />
          )}
          {connected ? (
            <Button
              type="submit"
              disabled={!form.isValid() || !ownedByMultisig}
            >
              Get auction PSBT
            </Button>
          ) : (
            <WalletConnectButton />
          )}
        </Flex>
        <SignedPsbtPreview signedAuctionPsbt={signedAuctionPsbt} />
      </form>

      {token.data && <PreviewImage token={token.data} />}
    </Flex>
  );
};
